<template>
    <primevue-button
        class="plain-btn dashboard-override-button"
        :class="{ 'has-period-progress': showBoaTimer }"
        @click="toggleModal"
    >
        <live-period-line
            v-if="showBoaTimer"
            ref="periodProgress"
            class="dashboard-period-progress"
            :use-trading-period="useTradingPeriod"
        />
        <span
            v-if="overwrittenDatesNumber"
            class="total-overwritten mobile-only"
        >{{ overwrittenDatesNumber }}</span>
        <div v-if="dateFilterNumbers" class="no-wrap desktop-only flex-row-with-gap">
            <primevue-tag
                v-for="(overrideTag, index) in combinedDateOverrideTags"
                :key="index"
                v-tooltip.bottom="overrideTag.tooltip"
                class="date-override-string"
                :value="overrideTag.value"
            />
        </div>
        <countdown-timer
            v-if="props.dateOverrides?.boa"
            v-show="showBoaTimer"
            :use-trading-period="useTradingPeriod"
            @finished="triggerAutoRollover"
            ref="countdownTimer"
        />
        <template v-if="!showBoaTimer">
            <span class="desktop-only">{{props.label ?? 'Override dashboard'}}</span>
        </template>
        <font-awesome-icon :icon="['fas', 'calendar-lines-pen']" />
    </primevue-button>
    <primevue-dialog
        dismissable-mask
        modal
        close-on-escape
        v-model:visible="showModal"
        header="Override dashboard filters"
    >
        <div class="dates-container">
            <template v-if="props.hasGenericCharts && !props.noDateOverride">
                <label>Charts</label>
                <div class="date-buttons">
                    <primevue-button
                        v-if="props.dateOverrides?.chart"
                        v-tooltip.bottom="'Reset'"
                        aria-label="Reset"
                        class="plain-btn"
                        @click="resetDateOverride(DateOverrideComponents.chart)"
                    >
                        Reset
                        <font-awesome-icon class="icon" :icon="['fas', 'rotate-left']" />
                    </primevue-button>
                    <primevue-button
                        v-tooltip.bottom="'Change date range'"
                        class="plain-btn"
                        @click="openDatePicker($event, DateOverrideComponents.chart)"
                    >
                        <div v-if="props.dateOverrides?.chart" class="flex-row-with-gap">
                            <primevue-tag v-for="(part, index) in dateStrings.chart.slice(0, MAX_DATE_FILTERS_TO_SHOW)" :key="index" class="no-wrap" :value="part" />
                            {{ dateStrings.chart.length > MAX_DATE_FILTERS_TO_SHOW ? `+${dateStrings.chart.length - MAX_DATE_FILTERS_TO_SHOW}` : '' }}
                        </div>
                        <div v-else>Set override</div>
                        <font-awesome-icon class="icon" :icon="['far', 'calendar-days']" />
                    </primevue-button>
                </div>
            </template>
            <template v-if="props.hasDateRangeLiveTables && !props.noDateOverride">
                <label>Live tables</label>
                <div class="date-buttons">
                    <primevue-button
                        v-if="props.dateOverrides?.liveTable"
                        v-tooltip.bottom="'Reset'"
                        aria-label="Reset"
                        class="plain-btn"
                        @click="resetDateOverride(DateOverrideComponents.liveTable)"
                    >
                        Reset
                        <font-awesome-icon class="icon" :icon="['fas', 'rotate-left']" />
                    </primevue-button>
                    <primevue-button
                        v-tooltip.bottom="'Change date range'"
                        class="plain-btn"
                        @click="openDatePicker($event, DateOverrideComponents.liveTable)"
                    >
                        <div v-if="props.dateOverrides?.liveTable">{{ dateStrings.liveTable }}</div>
                        <div v-else>Set override</div>
                        <font-awesome-icon class="icon" :icon="['far', 'calendar-days']" />
                    </primevue-button>
                </div>
            </template>
            <label v-if="props.hasBMComponents && !props.noDateOverride">BM Components</label>
            <div v-if="props.hasBMComponents && !props.noDateOverride" class="date-buttons">
                <primevue-button
                    v-if="props.dateOverrides?.boa"
                    v-tooltip.bottom="'Reset'"
                    class="plain-btn"
                    @click="resetDateOverride(DateOverrideComponents.boa)"
                >
                    Reset
                    <font-awesome-icon class="icon" :icon="['fas', 'rotate-left']" />
                </primevue-button>
                <primevue-button
                    v-tooltip.bottom="'Change date range'"
                    class="plain-btn"
                    @click="openDatePicker($event, DateOverrideComponents.boa)"
                >
                    {{props.dateOverrides?.boa ? dateStrings.boa : 'Set override'}}
                    <font-awesome-icon class="icon" :icon="['far', 'calendar-days']" />
                </primevue-button>
            </div>
            <template v-if="props.hasGenericCharts || props.noDateOverride">
                <label>
                    Use Shared Tooltip
                    <info-icon-with-tooltip :tooltip-text="'Syncs tooltips across all charts, so hovering over a data point in one chart will display the tooltip for the corresponding data point in all other charts.'" direction="right" />
                </label>
                <div class="date-buttons">
                    <checkbox
                        :model-value="dateOverrides?.commonSettings?.sharedTooltipAcrossModules ?? false"
                        :binary="true"
                        @update:model-value="updateCommonDashboardSettings(settings => settings.sharedTooltipAcrossModules = $event)"
                    />
                </div>
                <label>
                    Use Shared Legend Toggling
                    <info-icon-with-tooltip :tooltip-text="'Syncs legend show/hide actions across charts with matching series'" direction="right" />
                </label>
                <div class="date-buttons">
                    <checkbox
                        :model-value="dateOverrides?.commonSettings?.sharedLegendToggling ?? false"
                        :binary="true"
                        @update:model-value="updateCommonDashboardSettings(settings => settings.sharedLegendToggling = $event)"
                    />
                </div>
                <label>
                    Use Y Axis Normalisation
                    <info-icon-with-tooltip :tooltip-text="'Normalises the Y axis across charts for better comparison'" direction="right" />
                </label>
                <div class="date-buttons">
                    <checkbox
                        :model-value="dateOverrides?.commonSettings?.useYAxisNormalisation ?? false"
                        :binary="true"
                        @update:model-value="updateCommonDashboardSettings(settings => settings.useYAxisNormalisation = $event)"
                    />
                </div>
            </template>
            <template v-if="props.hasNivEvolution">
                <label>Share NIV evolution view finder</label>
                <div class="date-buttons">
                    <checkbox
                        :model-value="dateOverrides?.commonSettings?.sharedNivEvolutionViewfinder ?? false"
                        :binary="true"
                        @update:model-value="updateCommonDashboardSettings(settings => settings.sharedNivEvolutionViewfinder = $event)"
                    />
                </div>
            </template>
            <template v-if="props.hasStorageForecastAssetCharts">
                <label>Asset forecast context</label>
                <div class="date-buttons">
                    <primevue-button
                        v-if="props.dateOverrides?.storageForecast?.asset"
                        v-tooltip.bottom="'Reset'"
                        class="plain-btn"
                        @click="resetStorageForecastOverride('asset')"
                    >
                        Reset
                        <font-awesome-icon class="icon" :icon="['fas', 'rotate-left']" />
                    </primevue-button>
                    <primevue-button
                        v-tooltip.bottom="'Override quarter/scenario/percentile for asset forecast modules'"
                        aria-label="Override asset forecast context"
                        class="plain-btn"
                        :disabled="!props.storageForecastAssetCountries?.length"
                        @click="openDatePicker($event, 'storageForecastAsset')"
                    >
                        <div v-if="props.dateOverrides?.storageForecast?.asset" class="flex-row-with-gap">
                            <primevue-tag
                                v-for="(part, index) in dateStrings.storageForecastAsset.slice(0, MAX_DATE_FILTERS_TO_SHOW)"
                                :key="index"
                                class="no-wrap"
                                :value="part"
                            />
                            {{ dateStrings.storageForecastAsset.length > MAX_DATE_FILTERS_TO_SHOW ? `+${dateStrings.storageForecastAsset.length - MAX_DATE_FILTERS_TO_SHOW}` : '' }}
                        </div>
                        <div v-else>Set override</div>
                        <font-awesome-icon class="icon" :icon="['fas', 'sliders']" />
                    </primevue-button>
                </div>
            </template>
            <template v-if="props.hasStorageForecastSystemCharts">
                <label>LTF market charts context</label>
                <div class="date-buttons">
                    <primevue-button
                        v-if="props.dateOverrides?.storageForecast?.system"
                        v-tooltip.bottom="'Reset'"
                        class="plain-btn"
                        @click="resetStorageForecastOverride('system')"
                    >
                        Reset
                        <font-awesome-icon class="icon" :icon="['fas', 'rotate-left']" />
                    </primevue-button>
                    <primevue-button
                        v-tooltip.bottom="'Override quarter/scenario for long term forecasting market chart modules'"
                        aria-label="Override LTF market charts context"
                        class="plain-btn"
                        :disabled="!props.storageForecastSystemCountries?.length"
                        @click="openDatePicker($event, 'storageForecastSystem')"
                    >
                        <div v-if="props.dateOverrides?.storageForecast?.system" class="flex-row-with-gap">
                            <primevue-tag
                                v-for="(part, index) in dateStrings.storageForecastSystem.slice(0, MAX_DATE_FILTERS_TO_SHOW)"
                                :key="index"
                                class="no-wrap"
                                :value="part"
                            />
                            {{ dateStrings.storageForecastSystem.length > MAX_DATE_FILTERS_TO_SHOW ? `+${dateStrings.storageForecastSystem.length - MAX_DATE_FILTERS_TO_SHOW}` : '' }}
                        </div>
                        <div v-else>Set override</div>
                        <font-awesome-icon class="icon" :icon="['fas', 'sliders']" />
                    </primevue-button>
                </div>
            </template>
            <popover ref="datePickerOverlay" close-on-escape>
                <chart-date-picker
                    v-if="datePickerType === DateOverrideComponents.chart"
                    :selected-date="dateOverridesDatePicker.chart"
                    :dashboard-id="dashboardId"
                    :show-timezone="props.showTimezone"
                    :minimum-view="props.minimumView"
                    :min-date="props.minDate"
                    :max-date="props.maxDate"
                    :granularity-mode="chartGranularityMode"
                    :index-granularity-warning="props.hasIndexModules ? 'The selected granularity is not supported for index modules on this dashboard.' : undefined"
                    :days-only="props.daysOnly"
                    @update:selected-date="updateSelectedDate($event, DateOverrideComponents.chart)"
                    @update:timezone-settings="updateSelectedDate($event, DateOverrideComponents.chart, true)"
                />
                <chart-date-picker
                    v-if="datePickerType === DateOverrideComponents.liveTable"
                    :selected-date="dateOverridesDatePicker.liveTable"
                    :min-date="props.minDate"
                    :max-date="props.maxDate"
                    granularity-mode="none"
                    days-only
                    @update:selected-date="updateSelectedDate($event, DateOverrideComponents.liveTable)"
                />
                <advanced-date-picker
                    v-if="datePickerType === DateOverrideComponents.boa"
                    :model-value="dateOverridesDatePicker.chart"
                    :allowed-tabs="[AdvancedDatePickerTabsEnum.BOA_PERIOD]"
                    allow-select-boa-period
                    :boa-date-period="dateOverridesDatePicker.boa"
                    :boa-period-override-mode="periodOverrideMode"
                    @submit:boa-period="updateBoaPeriodOverride"
                />
                <div class="forecast-context-picker-wrapper" v-if="datePickerType === 'storageForecastAsset'">
                    <div v-if="selectedStorageForecastCountry" class="context-country-tabs">
                        <tabs
                            :value="selectedStorageForecastCountry"
                            scrollable
                            @update:value="selectStorageForecastCountry"
                        >
                            <tab-list>
                                <tab v-for="country in storageForecastCountryOptions" :key="country.id" :value="country.id">
                                    {{ country.name }}
                                </tab>
                            </tab-list>
                        </tabs>
                    </div>
                    <storage-forecast-context-picker
                        v-if="selectedStorageForecastCountry"
                        source-type="RunOutput"
                        show-percentile
                        :fixed-country="selectedStorageForecastCountry"
                        :model-value="storageForecastPickerModel"
                        @update:model-value="updateStorageForecastOverride($event, 'asset')"
                    />
                </div>
                <div class="forecast-context-picker-wrapper" v-if="datePickerType === 'storageForecastSystem'">
                    <div v-if="selectedStorageForecastCountry" class="context-country-tabs">
                        <tabs
                            :value="selectedStorageForecastCountry"
                            scrollable
                            @update:value="selectStorageForecastCountry"
                        >
                            <tab-list>
                                <tab v-for="country in storageForecastCountryOptions" :key="country.id" :value="country.id">
                                    {{ country.name }}
                                </tab>
                            </tab-list>
                        </tabs>
                    </div>
                    <storage-forecast-context-picker
                        v-if="selectedStorageForecastCountry"
                        source-type="SystemForecasts"
                        :fixed-country="selectedStorageForecastCountry"
                        :model-value="storageForecastPickerModel"
                        @update:model-value="updateStorageForecastOverride($event, 'system')"
                    />
                </div>
            </popover>
        </div>
        <template #footer>
            <div v-if="!props.noDateOverride" class="footer-element">You'll be able to revert your changes to the original dates any time</div>
        </template>
    </primevue-dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import Popover from 'primevue/popover';
import PrimevueDialog from 'primevue/dialog';
import PrimevueButton from 'primevue/button';
import Tabs from 'primevue/tabs';
import TabList from 'primevue/tablist';
import Tab from 'primevue/tab';

import { IAdvancedDatePickerModel, IAdvancedDatePickerUpdatePayload } from '@/interfaces/general/IAdvancedDatePickerModel.ts';
import {
    DateOverrideComponents,
    IDashboardCommonSettings,
    IDashboardOverrides,
    IDashboardStorageForecastContext,
    IDashboardStorageForecastCountryOverrides,
    TDashboardStorageForecastOverrideKind,
} from '@/interfaces/powerMarkets/dashboard/IDashboard.ts';
import { IDateObject } from '@/interfaces/powerMarkets/IDateObject.ts';
import {
    generateDatePickerSettings,
    getLocalDatePickerSettingsString,
    getDateObjectFromDatePicker,
    getDatePeriodString,
    areDatePeriodsEqual,
} from '@/utils/dateUtils.ts';
import { IDatePeriod } from '@/interfaces/powerMarkets/balancing/IDatePeriod.ts';
import CountdownTimer from '@/components/common/CountdownTimer.vue';
import LivePeriodLine from '@/components/powerMarkets/balancing/LivePeriodLine.vue';
import ChartDatePicker from '@/components/powerMarkets/chart/ChartDatePicker.vue';
import { PeriodTypeOverridesWithInherited } from '@/types/powerMarkets/balancing/PeriodTypeOverrides.ts';
import { getPeriodPageOverride, getPeriodToDisplayComputed } from '@/composables/usePeriodToDisplay.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { useDataStoreActions } from '@/actions/powerMarkets/dataStoreActions.ts';
import Checkbox from 'primevue/checkbox';
import InfoIconWithTooltip from '@/components/common/InfoIconWithTooltip.vue';
import { PERIOD_FREQUENCY_OPTIONS } from '@/constants/PeriodFrequencyOptions.ts';
import { aggregateGranularities } from '@/constants/powerMarkets/ChartAggregateOptions.ts';
import { INDEX_GRANULARITY_OPTIONS } from '@/constants/benchmarks/IndexConstants.ts';
import PrimevueTag from 'primevue/tag';
import { TDatePickerMinimumView } from '@/types/powerMarkets/TDatePickerMinimumView.ts';
import { TDatePickerGranularityMode } from '@/types/powerMarkets/TDatePickerGranularityMode.ts';
import AdvancedDatePicker from '@/components/powerMarkets/date-picker/AdvancedDatePicker.vue';
import { AdvancedDatePickerTabsEnum } from '@/enums/general/AdvancedDatePickerTabsEnum.ts';
import StorageForecastContextPicker from '@/components/storageForecasting/StorageForecastContextPicker.vue';
import {
    getStorageForecastContextSummary,
    getStorageForecastOverrideContexts,
    IStorageForecastContextSelection,
} from '@/utils/storageForecasting/storageForecastContextUtils.ts';
import { TStorageForecastCountries } from '@/types/TStorageForecastCountries.ts';

const userSettingsStore = useUserSettingsStore();
const dataStoreActions = useDataStoreActions();

const MAX_DATE_FILTERS_TO_SHOW = 2;

type DateRangeOverrideType = DateOverrideComponents.chart | DateOverrideComponents.liveTable;
type DashboardStorageForecastPickerType = 'storageForecastAsset' | 'storageForecastSystem';
type DashboardDatePickerType = DateRangeOverrideType | DateOverrideComponents.boa | DashboardStorageForecastPickerType;

const props = defineProps<{
    label?: string;
    dateOverrides?: IDashboardOverrides | null;
    defaultDateOverrides?: IDashboardOverrides | null;
    hasBMComponents?: boolean;
    hasGenericCharts?: boolean;
    hasIndexModules?: boolean;
    hasDateRangeLiveTables?: boolean;
    noDateOverride?: boolean;
    dashboardId?: string;
    hasNivEvolution?: boolean;
    showTimezone?: boolean;
    minimumView?: TDatePickerMinimumView;
    granularityMode?: TDatePickerGranularityMode;
    daysOnly?: boolean;
    minDate?: Date;
    maxDate?: Date;
    temporaryBoaPeriod?: IDatePeriod;
    hasStorageForecastAssetCharts?: boolean;
    hasStorageForecastSystemCharts?: boolean;
    storageForecastAssetCountries?: Array<TStorageForecastCountries>;
    storageForecastSystemCountries?: Array<TStorageForecastCountries>;
}>();

const emit = defineEmits<{
    'update:current-dates-override': [IDashboardOverrides];
    'update:temporary-boa-period': [IDatePeriod | undefined];
}>();

const countdownTimer = ref<InstanceType<typeof CountdownTimer> | null>(null);
const periodProgress = ref<InstanceType<typeof LivePeriodLine> | null>(null);

const showModal = ref(false);

const datePickerOverlay = ref<InstanceType<typeof Popover> | null>(null);

const datePickerType = ref<DashboardDatePickerType>(DateOverrideComponents.chart);
const selectedStorageForecastCountry = ref<TStorageForecastCountries | null>(null);
const storageForecastCountryOptions = computed(() => {
    const availableCountries = datePickerType.value === 'storageForecastAsset'
        ? props.storageForecastAssetCountries
        : props.storageForecastSystemCountries;
    return (availableCountries ?? []).map(country => ({ id: country, name: country }));
});

watch(storageForecastCountryOptions, options => {
    if (!options.some(option => option.id === selectedStorageForecastCountry.value)) {
        selectedStorageForecastCountry.value = options[0]?.id ?? null;
    }
}, { immediate: true });

const periodOverrideMode = ref<PeriodTypeOverridesWithInherited>('inherited');
const inheritedPeriodMode = getPeriodPageOverride();
const useTradingPeriod = computed(() => (periodOverrideMode.value === 'inherited' ? inheritedPeriodMode.value : periodOverrideMode.value) === 'trading');

const currentPeriodToDisplay = getPeriodToDisplayComputed(periodOverrideMode);

const dateOverridesDatePicker = ref<{
    [DateOverrideComponents.chart]: IAdvancedDatePickerModel;
    [DateOverrideComponents.liveTable]: IAdvancedDatePickerModel;
    [DateOverrideComponents.boa]: IDatePeriod;
    [DateOverrideComponents.commonSettings]: IDashboardCommonSettings;
}>({
    chart: generateDatePickerSettings(),
    liveTable: generateDatePickerSettings(),
    boa: {
        date: new Date(),
        period: 1,
    },
    commonSettings: {
        sharedTooltipAcrossModules: false,
        sharedLegendToggling: false,
    },
});

const showBoaTimer = computed(() => userSettingsStore.userSettings.otherSettings.data.showTimer
    && !!props.dateOverrides?.boa
    && areDatePeriodsEqual(currentPeriodToDisplay.value, dateOverridesDatePicker.value.boa));

// maybe move this to store?
const chartDateOverridesChanged = computed<Array<'date' | 'timezone' | 'granularity' | 'periods'>>(() => {
    const chartOverrides = props.dateOverrides?.chart;
    if (!chartOverrides) return [];

    const parts: Array<'date' | 'timezone' | 'granularity' | 'periods'> = [];
    if (chartOverrides.from && chartOverrides.to) {
        parts.push('date');
    }
    if (chartOverrides.timezone) {
        parts.push('timezone');
    }
    if (chartOverrides.aggregateOptions) {
        parts.push('granularity');
    }
    if (chartOverrides.periodsPerDay) {
        parts.push('periods');
    }
    return parts;
});

const dateFilterNumbers = computed(() => {
    let counter = 0;
    if (props.dateOverrides?.boa && (props.defaultDateOverrides ? JSON.stringify(props.dateOverrides.boa) !== JSON.stringify(props.defaultDateOverrides.boa) : true)) counter += 1;
    if (props.dateOverrides?.chart && (props.defaultDateOverrides ? JSON.stringify(props.dateOverrides.chart) !== JSON.stringify(props.defaultDateOverrides.chart) : true)) counter += chartDateOverridesChanged.value.length;
    if (props.dateOverrides?.liveTable) counter += 1;
    if (props.dateOverrides?.storageForecast?.asset) counter += getStorageForecastOverrideContexts(props.dateOverrides.storageForecast.asset).length;
    if (props.dateOverrides?.storageForecast?.system) counter += getStorageForecastOverrideContexts(props.dateOverrides.storageForecast.system).length;
    return counter;
});

const overwrittenDatesNumber = computed(() => {
    let counter = dateFilterNumbers.value;
    if (props.dateOverrides?.commonSettings?.sharedTooltipAcrossModules) counter += 1;
    if (props.dateOverrides?.commonSettings?.sharedLegendToggling) counter += 1;
    if (props.dateOverrides?.commonSettings?.useYAxisNormalisation) counter += 1;
    return counter;
});

const dateStrings = computed(() => ({
    chart: getOverrideDatePickerSettingsString(dateOverridesDatePicker.value.chart),
    liveTable: getLocalDatePickerSettingsString(dateOverridesDatePicker.value.liveTable),
    boa: getDatePeriodString(dateOverridesDatePicker.value.boa),
    storageForecastAsset: getStorageForecastOverrideStrings('asset'),
    storageForecastSystem: getStorageForecastOverrideStrings('system'),
}));

const chartGranularityMode = computed<TDatePickerGranularityMode>(() => props.granularityMode ?? 'standard');
const storageForecastPickerModel = computed<IStorageForecastContextSelection | null>(() => {
    if (!selectedStorageForecastCountry.value) return null;
    if (datePickerType.value === 'storageForecastAsset') {
        return props.dateOverrides?.storageForecast?.asset?.[selectedStorageForecastCountry.value] ?? null;
    }
    return props.dateOverrides?.storageForecast?.system?.[selectedStorageForecastCountry.value] ?? null;
});

function getOverrideDatePickerSettingsString (dateOverrides: IAdvancedDatePickerModel): Array<string> {
    const parts: string[] = [];
    if (chartDateOverridesChanged.value.includes('date')) {
        parts.push(getLocalDatePickerSettingsString(dateOverrides, undefined, props.minimumView ?? 'day'));
    }
    if (chartDateOverridesChanged.value.includes('granularity')) {
        const granularityId = dateOverrides?.aggregate?.granularity;
        const granularityOptions = chartGranularityMode.value === 'index' ? INDEX_GRANULARITY_OPTIONS : aggregateGranularities;
        const granularityString = granularityOptions.find((option) => option.id === granularityId)?.name;
        if (granularityString) parts.push(granularityString);
    }
    if (chartDateOverridesChanged.value.includes('timezone')) {
        const timezone = dateOverrides.timezone || dateOverrides?.timezone;
        if (timezone) parts.push(timezone);
    }
    if (chartDateOverridesChanged.value.includes('periods')) {
        const periodsPerDay = dateOverrides.periodsPerDay ?? dateOverrides?.periodsPerDay;
        const periodOption = PERIOD_FREQUENCY_OPTIONS.find((option) => option.id === periodsPerDay?.toString());
        parts.push(periodOption?.name || '');
    }

    return parts;
}

function getStorageForecastOverrideStrings (kind: TDashboardStorageForecastOverrideKind): Array<string> {
    if (kind === 'asset') {
        return getStorageForecastOverrideContexts(props.dateOverrides?.storageForecast?.asset)
            .map(override => getStorageForecastContextSummary(override, 'RunOutput'))
            .filter(Boolean);
    }

    return getStorageForecastOverrideContexts(props.dateOverrides?.storageForecast?.system)
        .map(override => getStorageForecastContextSummary(override, 'SystemForecasts'))
        .filter(Boolean);
}

const combinedDateOverrideTags = computed<Array<{ value: string; tooltip: string }>>(() => {
    const tags = [];
    if (props.dateOverrides?.chart) {
        tags.push(...dateStrings.value.chart.map(value => ({ value, tooltip: 'Chart override' })));
    }
    if (props.dateOverrides?.liveTable) {
        tags.push({ value: dateStrings.value.liveTable, tooltip: 'Live table override' });
    }
    if (props.dateOverrides?.boa) {
        tags.push({ value: dateStrings.value.boa, tooltip: 'BM components override' });
    }
    if (props.dateOverrides?.storageForecast?.asset) {
        tags.push(...dateStrings.value.storageForecastAsset.map(value => ({ value, tooltip: 'Asset forecast override' })));
    }
    if (props.dateOverrides?.storageForecast?.system) {
        tags.push(...dateStrings.value.storageForecastSystem.map(value => ({ value, tooltip: 'System forecast override' })));
    }
    return tags;
});

async function triggerAutoRollover (): Promise<void> {
    if (props.dateOverrides?.boa && userSettingsStore.userSettings.otherSettings.data.autoRollover) {
        await dataStoreActions.getPeriodData();
        if (countdownTimer.value) {
            countdownTimer.value.restart();
        }
        periodProgress.value?.restart();
    }
}

function getDateOverrideBoaValue (): IDatePeriod {
    if (props.temporaryBoaPeriod) {
        return props.temporaryBoaPeriod;
    }
    if (!props.dateOverrides?.boa) {
        return currentPeriodToDisplay.value;
    }
    if (typeof props.dateOverrides.boa === 'string') {
        return currentPeriodToDisplay.value;
    }
    return props.dateOverrides.boa as IDatePeriod;
}

function getExistingStorageForecastCountry (type: DashboardStorageForecastPickerType): TStorageForecastCountries | null {
    const overrides = type === 'storageForecastAsset'
        ? props.dateOverrides?.storageForecast?.asset
        : props.dateOverrides?.storageForecast?.system;
    return storageForecastCountryOptions.value.find(option => overrides?.[option.id])?.id ?? null;
}

function selectStorageForecastCountry (country: string | number | null): void {
    const option = storageForecastCountryOptions.value.find(item => item.id === country);
    if (option) selectedStorageForecastCountry.value = option.id;
}

function openDatePicker (event: Event, type: DashboardDatePickerType): void {
    datePickerType.value = type;
    if (type === 'storageForecastAsset' || type === 'storageForecastSystem') {
        selectedStorageForecastCountry.value = getExistingStorageForecastCountry(type)
            ?? storageForecastCountryOptions.value[0]?.id
            ?? null;
    }
    datePickerOverlay.value?.show(event);
}

function toggleModal (): void {
    showModal.value = !showModal.value;
}

function updateCommonDashboardSettings (updateSettings: (settings: IDashboardCommonSettings) => void): void {
    updateSettings(dateOverridesDatePicker.value.commonSettings);
    const newOptions = {
        ...props.dateOverrides,
        commonSettings: dateOverridesDatePicker.value.commonSettings,
    };
    emit('update:current-dates-override', newOptions);
    datePickerOverlay.value?.hide();
}

function updateBoaPeriodOverride (datePeriod: IDatePeriod, periodOverrideModeSelected: PeriodTypeOverridesWithInherited): void {
    periodOverrideMode.value = periodOverrideModeSelected;
    dateOverridesDatePicker.value.boa = datePeriod;
    emit('update:temporary-boa-period', datePeriod);
    emit('update:current-dates-override', {
        ...props.dateOverrides,
        boa: periodOverrideModeSelected,
    });
    datePickerOverlay.value?.hide();
    showModal.value = false;
}

function isAdvancedDatePickerUpdatePayload (value: unknown): value is IAdvancedDatePickerUpdatePayload {
    if (!value || typeof value !== 'object') return false;

    return [
        'dateRange',
        'aggregate',
        'dateType',
        'rangeSelectType',
        'isHourRangeEnabled',
        'hoursBefore',
        'hoursAfter',
        'daysBefore',
        'daysAfter',
        'timezone',
        'periodsPerDay',
    ].some(key => key in value);
}

function getCurrentDateObjectOverride (type: DateRangeOverrideType): IDateObject {
    const dateOverride = props.dateOverrides?.[type];
    return dateOverride ? { ...dateOverride } : {};
}

function buildDateObjectFromPartialUpdate (
    updatePayload: IAdvancedDatePickerUpdatePayload,
    type: DateRangeOverrideType,
    isTimezoneTab: boolean,
): IDateObject {
    if (isTimezoneTab) {
        const currentOverride = getCurrentDateObjectOverride(type);

        return {
            ...currentOverride,
            timezone: updatePayload.timezone ?? null,
            periodsPerDay: updatePayload.periodsPerDay ?? null,
        };
    }

    if (updatePayload.dateRange) {
        const currentOverride = getCurrentDateObjectOverride(type);
        const currentDatePickerSettings = dateOverridesDatePicker.value[type];

        return {
            ...currentOverride,
            ...getDateObjectFromDatePicker(updatePayload as IAdvancedDatePickerModel, false),
            ...(currentDatePickerSettings.timezone !== undefined ? { timezone: currentDatePickerSettings.timezone } : {}),
            ...(currentDatePickerSettings.periodsPerDay !== undefined ? { periodsPerDay: currentDatePickerSettings.periodsPerDay } : {}),
        };
    }

    const currentOverride = getCurrentDateObjectOverride(type);
    const updatedOverride: IDateObject = {
        ...currentOverride,
    };

    if ('aggregate' in updatePayload) {
        updatedOverride.aggregateOptions = updatePayload.aggregate ? { ...updatePayload.aggregate } : null;
    }

    if ('timezone' in updatePayload) {
        updatedOverride.timezone = updatePayload.timezone ?? null;
    }

    if ('periodsPerDay' in updatePayload) {
        updatedOverride.periodsPerDay = updatePayload.periodsPerDay ?? null;
    }

    return updatedOverride;
}

function updateSelectedDate (date: IAdvancedDatePickerUpdatePayload | boolean | null, type: DateRangeOverrideType, isTimezoneTab = false): void {
    if (date === null) {
        datePickerOverlay.value?.hide();
        showModal.value = false;
        return;
    }
    if (isAdvancedDatePickerUpdatePayload(date)) {
        dateOverridesDatePicker.value[type] = {
            ...dateOverridesDatePicker.value[type],
            ...date,
        };

        const updatedDateOverride = buildDateObjectFromPartialUpdate(date, type, isTimezoneTab);
        const newOptions = {
            ...(props.dateOverrides ?? {}),
            [type]: updatedDateOverride,
        };
        emit('update:current-dates-override', newOptions);
    }
    datePickerOverlay.value?.hide();
    showModal.value = false;
}

function toDashboardStorageForecastContext (selection: IStorageForecastContextSelection): IDashboardStorageForecastContext {
    return {
        country: selection.country ?? null,
        quarter: selection.quarter ?? null,
        scenarioId: selection.scenarioId ?? null,
        scenarioName: selection.scenarioName ?? null,
        scenarioLabel: selection.scenarioLabel ?? selection.scenarioName ?? null,
        percentile: selection.percentile ?? null,
    };
}

function updateStorageForecastOverride (
    selection: IStorageForecastContextSelection,
    kind: TDashboardStorageForecastOverrideKind,
): void {
    if (!selectedStorageForecastCountry.value) return;
    const override: IDashboardStorageForecastCountryOverrides = {
        ...(props.dateOverrides?.storageForecast?.[kind] ?? {}),
        [selectedStorageForecastCountry.value]: toDashboardStorageForecastContext(selection),
    };
    emit('update:current-dates-override', {
        ...(props.dateOverrides ?? {}),
        storageForecast: {
            ...(props.dateOverrides?.storageForecast ?? {}),
            [kind]: override,
        },
    });
    datePickerOverlay.value?.hide();
    showModal.value = false;
}

function resetDateOverride (type: DateRangeOverrideType | DateOverrideComponents.boa): void {
    if (type === DateOverrideComponents.boa) {
        periodOverrideMode.value = 'inherited';
        dateOverridesDatePicker.value[type] = currentPeriodToDisplay.value;
        emit('update:temporary-boa-period', undefined);
    } else {
        resetDateOverrides(type);
    }
    const newOptions = {
        ...props.dateOverrides,
        [type]: null,
    };

    emit('update:current-dates-override', newOptions);
    showModal.value = false;
}

function resetDateOverrides (type: DateRangeOverrideType): void {
    if (type === DateOverrideComponents.chart) {
        if (props.defaultDateOverrides?.chart) {
            dateOverridesDatePicker.value.chart = generateDatePickerSettings(
                undefined,
                props.defaultDateOverrides.chart,
                props.defaultDateOverrides.chart.aggregateOptions,
            );
            dateOverridesDatePicker.value.chart.timezone = props.defaultDateOverrides.chart.timezone ?? null;
            dateOverridesDatePicker.value.chart.periodsPerDay = props.defaultDateOverrides.chart.periodsPerDay ?? null;
        } else {
            dateOverridesDatePicker.value.chart = generateDatePickerSettings();
        }
    }

    if (type === DateOverrideComponents.liveTable) dateOverridesDatePicker.value.liveTable = generateDatePickerSettings();

    dateOverridesDatePicker.value.boa = getDateOverrideBoaValue();
}

function resetStorageForecastOverride (kind: TDashboardStorageForecastOverrideKind): void {
    const storageForecast = { ...(props.dateOverrides?.storageForecast ?? {}) };
    delete storageForecast[kind];

    const newOptions: IDashboardOverrides = {
        ...(props.dateOverrides ?? {}),
        storageForecast,
    };
    if (!storageForecast.asset && !storageForecast.system) delete newOptions.storageForecast;

    emit('update:current-dates-override', newOptions);
    showModal.value = false;
}

function setDateOverrides (): void {
    if (props.dateOverrides?.chart) {
        dateOverridesDatePicker.value.chart = generateDatePickerSettings(undefined, props.dateOverrides.chart, props.dateOverrides.chart.aggregateOptions);
        dateOverridesDatePicker.value.chart.timezone = props.dateOverrides.chart.timezone ?? null;
        dateOverridesDatePicker.value.chart.periodsPerDay = props.dateOverrides.chart.periodsPerDay ?? null;
    }
    if (props.dateOverrides?.liveTable) {
        dateOverridesDatePicker.value.liveTable = generateDatePickerSettings(undefined, props.dateOverrides.liveTable);
    }
    if (typeof props.dateOverrides?.boa === 'string') {
        periodOverrideMode.value = props.dateOverrides.boa;
    }

    if (props.dateOverrides?.commonSettings) {
        dateOverridesDatePicker.value.commonSettings = props.dateOverrides.commonSettings;
    }

    dateOverridesDatePicker.value.boa = getDateOverrideBoaValue();
}

watch(
    () => props.dateOverrides,
    () => {
        setDateOverrides();
    },
    { immediate: true, deep: true },
);

watch(currentPeriodToDisplay, (newPeriod, oldPeriod) => {
    if (!props.dateOverrides?.boa || !userSettingsStore.userSettings.otherSettings.data.autoRollover) return;
    if (areDatePeriodsEqual(newPeriod, oldPeriod) || !areDatePeriodsEqual(dateOverridesDatePicker.value.boa, oldPeriod)) return;

    dateOverridesDatePicker.value.boa = newPeriod;
    emit('update:temporary-boa-period', newPeriod);
    countdownTimer.value?.restart();
    periodProgress.value?.restart();
});

</script>

<style lang="scss" scoped>
.dashboard-override-button {
    position: relative;
    overflow: hidden;

    &.has-period-progress {
        padding-top: var(--defaultSpacing);
    }

    .dashboard-period-progress {
        position: absolute;
        top: 0;
        left: 0;
        border-radius: 0;
        pointer-events: none;
    }
}

.dates-container {
    padding: var(--defaultSpacing);
    display: grid;
    grid-template-columns: 1fr auto;
    gap: var(--largeSpacing);
    align-items: center;

    @include responsive(sm) {
        grid-template-rows: repeat(auto-fill, 40px);
    }
}

.footer-element{
    font-size: var(--smallFontSize);
}

.date-buttons{
    display: flex;
    justify-content: flex-end;
    gap: var(--defaultSpacing);
    @include responsive('sm') {
        justify-content: flex-start;
    }
}

.total-overwritten{
    background: var(--neutral0);
    padding: 2px var(--defaultSpacing);
    border-radius: var(--borderRadiusInner);
    font-weight: bold;
    font-size: var(--smallFontSize);
}
.date-override-string {
    background: var(--neutral200);
}

.forecast-context-picker-wrapper {
    padding: var(--defaultSpacing);

    .context-country-tabs {
        margin-bottom: var(--defaultSpacing);
    }
}
</style>
